/**
 * Audit Logger — Internal debugging tool
 *
 * Writes structured audit entries to the `_auditLog` Firestore collection.
 * This collection is:
 *   - WRITE-ONLY from the client (append-only, no reads from app UI)
 *   - LOCKED by Firestore security rules (consumers cannot read it)
 *   - Readable only via Firebase Console or admin SDK
 *
 * Do NOT import this in UI components. Use only in service layers.
 *
 * Entry shape:
 *   {
 *     action:      string   — e.g. "INVOICE_CREATED", "PAYMENT_IN_RECORDED"
 *     status:      "OK" | "ERROR"
 *     collection:  string   — primary Firestore collection affected
 *     docId:       string?  — primary document ID (if known)
 *     actor:       string   — auth user email or uid
 *     payload:     object   — sanitized summary of the write (no PII beyond names)
 *     error:       string?  — error message if status === "ERROR"
 *     ts:          Timestamp
 *     env:         "production" | "development"
 *   }
 */

import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firestore.js";
import { auth } from "../firebase/auth.js";

const AUDIT_COLLECTION = "_auditLog";
const ENV = import.meta.env?.MODE || "development";

/**
 * Appends a single audit entry to `_auditLog`.
 * Fire-and-forget — never throws so it never breaks a business operation.
 *
 * @param {string} action   — Uppercase snake_case action name, e.g. "INVOICE_CREATED"
 * @param {object} payload  — Lightweight summary object (keep it small, no full line items)
 * @param {"OK"|"ERROR"} status
 * @param {string|null} errorMsg
 */
export async function writeAudit(action, payload = {}, status = "OK", errorMsg = null) {
  try {
    const actor = auth.currentUser?.email || auth.currentUser?.uid || "anonymous";

    // Auto-generate a time-based doc ID: ACTION_YYYYMMDDHHmmss_randomSuffix
    const now = new Date();
    const stamp = now.toISOString().replace(/[-:T.Z]/g, "").slice(0, 14);
    const rand = Math.random().toString(36).slice(2, 6);
    const entryId = `${action}_${stamp}_${rand}`;

    const entry = {
      action,
      status,
      actor,
      env: ENV,
      payload: _sanitize(payload),
      ts: serverTimestamp(),
      ...(errorMsg ? { error: String(errorMsg).slice(0, 500) } : {})
    };

    await setDoc(doc(collection(db, AUDIT_COLLECTION), entryId), entry);
  } catch (auditErr) {
    // Audit write failure MUST NOT surface to the user — only log to console in dev
    if (ENV !== "production") {
      console.warn("[AuditLogger] Failed to write audit entry:", auditErr);
    }
  }
}

/**
 * Convenience wrapper: wraps an async service call, logs OK or ERROR automatically.
 *
 * Usage:
 *   const result = await auditWrap("INVOICE_CREATED", { invoiceNo, customerId }, async () => {
 *     return await createInvoiceWithStockDeduction(data);
 *   });
 *
 * @param {string} action
 * @param {object} context    — static metadata known before the call
 * @param {Function} fn       — async function to execute and audit
 */
export async function auditWrap(action, context = {}, fn) {
  const start = Date.now();
  try {
    const result = await fn();
    const elapsed = Date.now() - start;
    // Fire-and-forget; don't await here so it doesn't slow down the caller
    writeAudit(action, { ...context, elapsedMs: elapsed }, "OK");
    return result;
  } catch (err) {
    const elapsed = Date.now() - start;
    writeAudit(action, { ...context, elapsedMs: elapsed }, "ERROR", err?.message);
    throw err; // Re-throw so the caller still handles it
  }
}

/**
 * Strips any fields that should not be in audit logs (binary data, full arrays, etc.)
 * Keeps the object small and safe for Firestore.
 */
function _sanitize(payload) {
  const SKIP_KEYS = new Set(["lineItems", "allocations", "paymentHistory", "token", "password"]);
  const MAX_STRING = 200;

  const out = {};
  for (const [k, v] of Object.entries(payload || {})) {
    if (SKIP_KEYS.has(k)) {
      // Record count only, not full array
      if (Array.isArray(v)) out[`${k}Count`] = v.length;
      continue;
    }
    if (typeof v === "string") {
      out[k] = v.slice(0, MAX_STRING);
    } else if (typeof v === "number" || typeof v === "boolean" || v === null) {
      out[k] = v;
    } else if (Array.isArray(v)) {
      out[`${k}Count`] = v.length;
    } else if (typeof v === "object") {
      out[k] = "[object]"; // Don't recurse into nested objects
    }
  }
  return out;
}
